import React from "react";

interface RoutePagerProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  // Extra content (e.g. the "Post N Requests" button), rendered centered in
  // its own sticky bar, separate from the pager.
  rightContent?: React.ReactNode;
}

// Compact "‹ N / M ›" pager pill used under the route search results.
const RoutePager: React.FC<RoutePagerProps> = ({
  page,
  totalPages,
  onPageChange,
  rightContent,
}) => {
  const showPager = totalPages > 1;
  if (!showPager && !rightContent) return null;

  return (
    <>
      {/* Post button: sticky (not fixed) so it pins to the bottom of the
          scroll area while there are results below, then settles into flow
          above the footer at the very end — so it never covers the footer. */}
      {rightContent && (
        <div className="sticky bottom-0 z-40 flex items-center justify-center px-4 pt-8 pb-4 bg-gradient-to-t from-[#f1f3f6] via-[#f1f3f6]/90 to-transparent pointer-events-none">
          <div className="pointer-events-auto">{rightContent}</div>
        </div>
      )}

      {/* Pager: plain page content, sits in normal flow at the bottom of the
          page (not sticky/pinned), so it scrolls with the results. */}
      {showPager && (
        <div className="flex items-center justify-center px-4 py-4">
          <div className="inline-flex items-center bg-[#1F2937] rounded-full px-1 py-1 gap-1 shadow-lg">
            <button
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              className="w-8 h-8 flex items-center justify-center rounded-full text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <span className="text-white text-sm font-medium px-3">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-full text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default RoutePager;
